import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DataProcessingAgreement = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-bissa-blue hover:text-bissa-gold transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-4xl font-bold text-bissa-blue mb-2">Data Processing Agreement</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          <h2>1. Introduction</h2>
          <p>
            This Data Processing Agreement ("DPA") forms part of the Terms of Service between Bissa.ai 
            ("Processor") and the customer ("Controller") regarding the processing of Personal Data 
            in connection with the Bissa.ai CRM service.
          </p>

          <h2>2. Definitions</h2>
          <ul>
            <li><strong>Controller:</strong> The customer who determines the purposes and means of processing Personal Data</li>
            <li><strong>Processor:</strong> Bissa.ai, which processes Personal Data on behalf of the Controller</li>
            <li><strong>Personal Data:</strong> Any information relating to an identified or identifiable natural person</li>
            <li><strong>Processing:</strong> Any operation performed on Personal Data, including collection, storage, and use</li>
            <li><strong>Data Subject:</strong> The individual whose Personal Data is being processed</li>
            <li><strong>GDPR:</strong> General Data Protection Regulation (EU) 2016/679</li>
          </ul>

          <h2>3. Scope and Nature of Processing</h2>
          
          <h3>Subject Matter</h3>
          <p>Processing of Personal Data necessary for the provision of Bissa.ai CRM services.</p>

          <h3>Duration</h3>
          <p>Processing will continue for the duration of the service agreement and data retention period.</p>

          <h3>Nature and Purpose</h3>
          <p>Processing is limited to the following purposes:</p>
          <ul>
            <li>Provision of CRM functionality (contact management, sales tracking)</li>
            <li>Data storage, backup, and synchronization</li>
            <li>System maintenance and technical support</li>
            <li>Analytics and performance monitoring (aggregated, non-personal)</li>
          </ul>

          <h3>Categories of Personal Data</h3>
          <p>The following categories of Personal Data may be processed:</p>
          <ul>
            <li>Contact information (names, email addresses, phone numbers)</li>
            <li>Professional information (company, job title, business addresses)</li>
            <li>Communication records and interaction history</li>
            <li>Sales and transaction data</li>
            <li>Account and authentication information</li>
          </ul>

          <h3>Categories of Data Subjects</h3>
          <ul>
            <li>The Controller's customers and prospects</li>
            <li>The Controller's business contacts</li>
            <li>The Controller's employees and authorized users</li>
          </ul>

          <h2>4. Processor Obligations</h2>
          
          <h3>Lawful Processing</h3>
          <p>Bissa.ai commits to:</p>
          <ul>
            <li>Process Personal Data only on documented instructions from the Controller</li>
            <li>Ensure processing is lawful under applicable data protection laws</li>
            <li>Not process Personal Data for its own purposes</li>
            <li>Immediately inform the Controller if instructions appear to violate applicable law</li>
          </ul>

          <h3>Confidentiality</h3>
          <ul>
            <li>Ensure that persons authorized to process Personal Data are bound by confidentiality</li>
            <li>Maintain strict confidentiality of all Personal Data</li>
            <li>Provide regular training to staff on data protection requirements</li>
          </ul>

          <h3>Security Measures</h3>
          <p>Bissa.ai implements appropriate technical and organizational measures:</p>
          <ul>
            <li>Encryption of Personal Data in transit and at rest</li>
            <li>Regular security assessments and penetration testing</li>
            <li>Access controls and multi-factor authentication</li>
            <li>Regular security monitoring and incident response procedures</li>
            <li>Secure data centers with physical access controls</li>
          </ul>

          <h2>5. Sub-Processing</h2>
          
          <h3>Authorized Sub-Processors</h3>
          <p>The Controller provides general authorization for Bissa.ai to engage the following sub-processors:</p>
          <ul>
            <li><strong>Cloud Infrastructure:</strong> Amazon Web Services, Google Cloud Platform</li>
            <li><strong>Payment Processing:</strong> Stripe, Inc.</li>
            <li><strong>Analytics:</strong> Google Analytics (anonymized data only)</li>
            <li><strong>Customer Support:</strong> Intercom, Inc.</li>
          </ul>

          <h3>Sub-Processor Requirements</h3>
          <p>All sub-processors must:</p>
          <ul>
            <li>Provide the same level of data protection as required in this DPA</li>
            <li>Be bound by written agreements that include adequate data protection provisions</li>
            <li>Be subject to regular compliance audits</li>
          </ul>

          <h3>Changes to Sub-Processors</h3>
          <p>
            Bissa.ai will inform the Controller of any intended changes to sub-processors with 30 days' 
            notice, allowing the Controller to object or terminate the agreement.
          </p>

          <h2>6. Data Subject Rights</h2>
          
          <h3>Assistance with Rights Requests</h3>
          <p>Bissa.ai will assist the Controller in responding to Data Subject requests for:</p>
          <ul>
            <li>Access to Personal Data</li>
            <li>Rectification of inaccurate Personal Data</li>
            <li>Erasure of Personal Data</li>
            <li>Restriction of processing</li>
            <li>Data portability</li>
            <li>Objection to processing</li>
          </ul>

          <h3>Response Timeline</h3>
          <p>
            Bissa.ai will respond to Controller requests for assistance within 10 business days 
            and provide necessary technical and organizational measures to facilitate compliance.
          </p>

          <h2>7. Data Breach Notification</h2>
          
          <h3>Notification Requirements</h3>
          <p>In case of a Personal Data breach, Bissa.ai will:</p>
          <ul>
            <li>Notify the Controller without undue delay and no later than 72 hours after awareness</li>
            <li>Provide details of the breach, affected data categories, and estimated number of records</li>
            <li>Describe immediate and planned remediation measures</li>
            <li>Provide contact information for further inquiries</li>
            <li>Assist the Controller in notifying supervisory authorities and Data Subjects as required</li>
          </ul>

          <h2>8. Data Transfers</h2>
          
          <h3>International Transfers</h3>
          <p>
            Personal Data may be transferred to and processed in countries outside the EEA. 
            Such transfers are protected by:
          </p>
          <ul>
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Adequacy decisions where applicable</li>
            <li>Additional safeguards as required by applicable law</li>
          </ul>

          <h2>9. Data Retention and Deletion</h2>
          
          <h3>Retention Period</h3>
          <p>Personal Data will be retained only as long as necessary for the purposes outlined in this DPA:</p>
          <ul>
            <li>During the active service period</li>
            <li>For 30 days after service termination (for data recovery)</li>
            <li>As required by applicable law or legitimate business interests</li>
          </ul>

          <h3>Data Deletion</h3>
          <p>Upon termination of services, Bissa.ai will:</p>
          <ul>
            <li>Provide the Controller 30 days to export Personal Data</li>
            <li>Delete or return all Personal Data as instructed by the Controller</li>
            <li>Delete existing copies unless retention is required by law</li>
            <li>Provide written confirmation of deletion upon request</li>
          </ul>

          <h2>10. Audits and Compliance</h2>
          
          <h3>Audit Rights</h3>
          <p>The Controller has the right to:</p>
          <ul>
            <li>Conduct audits and inspections of Bissa.ai's data processing activities</li>
            <li>Request compliance documentation and certifications</li>
            <li>Engage third-party auditors (at Controller's expense)</li>
          </ul>

          <h3>Compliance Documentation</h3>
          <p>Bissa.ai will provide:</p>
          <ul>
            <li>SOC 2 Type II compliance reports</li>
            <li>Security certifications and attestations</li>
            <li>Evidence of technical and organizational measures</li>
            <li>Staff training records and confidentiality agreements</li>
          </ul>

          <h2>11. Liability and Indemnification</h2>
          <p>
            Each party's liability under this DPA is subject to the limitation of liability provisions 
            in the main service agreement. The parties agree to cooperate in good faith to minimize 
            any damages or penalties resulting from data protection violations.
          </p>

          <h2>12. Term and Termination</h2>
          <p>
            This DPA remains in effect for the duration of the service agreement and will automatically 
            terminate upon termination of the main agreement, subject to data retention obligations.
          </p>

          <div className="mt-12 p-6 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="text-purple-800 font-semibold mb-2">Data Protection Commitment</h3>
            <p className="text-purple-700">
              This Data Processing Agreement demonstrates our commitment to protecting Personal Data 
              and ensuring compliance with applicable data protection regulations. We take our 
              responsibilities as a data processor seriously and are committed to maintaining the 
              highest standards of data protection.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProcessingAgreement;